import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { Box, Button, Typography } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";

import NonLoggedInLayout from "../../layout/NonLoggedInLayout";
import SchoolAuthContext from "../../context/SchoolAuthContext";
import { schoolGoogleLogin } from "../../services/schoolPortalApi";
import { useToast } from "../../hook/useToast";

const schoolGoogleClientId =
  import.meta.env.VITE_SCHOOL_GOOGLE_CLIENT_ID ||
  import.meta.env.VITE_COUNSELLOR_GOOGLE_CLIENT_ID ||
  import.meta.env.VITE_GOOGLE_CLIENT_ID;

const getLoginErrorMessage = (error) => {
  const data = error?.response?.data;
  const errors = data?.errors || data;

  if (Array.isArray(errors?.non_field_errors)) {
    return errors.non_field_errors[0];
  }

  if (Array.isArray(errors?.detail)) {
    return errors.detail[0];
  }

  if (errors && typeof errors === "object") {
    const firstFieldError = Object.values(errors).find(Array.isArray)?.[0];
    if (firstFieldError) {
      return firstFieldError;
    }
  }

  return errors?.detail || data?.message || "Google login failed";
};

const SchoolGoogleLoginButton = ({ disabled, onGoogleLogin }) => {
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    scope: "openid email profile",
    ux_mode: "popup",
    onSuccess: ({ code }) => onGoogleLogin(code),
    onError: () => onGoogleLogin(null),
  });

  return (
    <Button
      type="button"
      disabled={disabled}
      onClick={() => googleLogin()}
      fullWidth
      variant="outlined"
      sx={{
        py: 1.2,
        borderRadius: "12px",
        borderColor: "#ECEFF4",
        color: "#5D667A",
        backgroundColor: "#fff",
        fontSize: "15px",
        fontWeight: 600,
        textTransform: "none",
        gap: 1,
        "&:hover": {
          borderColor: "#D9DEE8",
          backgroundColor: "#F8FAFC",
        },
      }}
    >
      <Box
        component="img"
        src="/assets/images/google.svg"
        alt=""
        sx={{ width: 20, height: 20 }}
      />
      Continue with Google
    </Button>
  );
};

SchoolGoogleLoginButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onGoogleLogin: PropTypes.func.isRequired,
};

const SchoolLoginPage = () => {
  const { showToast } = useToast();
  const { schoolUser, loginSchool } = useContext(SchoolAuthContext);
  const navigate = useNavigate();
  const [googleLoading, setGoogleLoading] = useState(false);

  if (schoolUser?.role === "SchoolPortal") {
    return <Navigate to="/school-portal/dashboard" replace />;
  }

  const completeSchoolLogin = (response) => {
    const accessToken = response?.data?.token?.access;

    if (!accessToken) {
      showToast("Login failed", "error");
      return;
    }

    loginSchool(
      accessToken,
      response.data.user,
      response.data.school,
      response.data.dashboard,
    );
    showToast("School Google login successful", "success");
    navigate("/school-portal/dashboard");
  };

  const handleGoogleLogin = async (code) => {
    if (!code) {
      showToast("Google sign-in was cancelled", "error");
      return;
    }

    setGoogleLoading(true);
    try {
      const response = await schoolGoogleLogin({ code });
      completeSchoolLogin(response);
    } catch (error) {
      showToast(getLoginErrorMessage(error), "error");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <NonLoggedInLayout
      leftImage="/assets/images/admin-dashboard-login.png"
      leftImageAlt="School portal login"
    >
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 450,
            p: 4,
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: 700,
                color: "#2D315F",
                letterSpacing: 0,
                lineHeight: 1.1,
              }}
            >
              School
            </Typography>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: 700,
                color: "#FFB04A",
                letterSpacing: 0,
                lineHeight: 1.1,
              }}
            >
              Portal
            </Typography>
            <Box
              component="span"
              sx={{
                display: "block",
                width: 60,
                height: 3,
                bgcolor: "#2D315F",
                mt: 1,
                borderRadius: 2,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: "14px",
              color: "rgba(129, 143, 171, 1)",
              mt: 1,
              mb: 3,
              fontWeight: 500,
            }}
          >
            Sign in with the Google account registered for your school
          </Typography>

          {schoolGoogleClientId ? (
            <GoogleOAuthProvider clientId={schoolGoogleClientId}>
              <SchoolGoogleLoginButton
                disabled={googleLoading}
                onGoogleLogin={handleGoogleLogin}
              />
            </GoogleOAuthProvider>
          ) : (
            <Button
              type="button"
              disabled
              fullWidth
              variant="outlined"
              sx={{
                py: 1.2,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Continue with Google
            </Button>
          )}
        </Box>
      </Box>
    </NonLoggedInLayout>
  );
};

export default SchoolLoginPage;
